import React, { useEffect, useMemo, useState} from 'react';
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE, dataTaggerActionClear, dataTaggerActionSet } from '@laramie/ui';

import { APIS } from '../../config/apis';
import { CondicionesAplicacion, CriteriosCaducidad, CriteriosFinanciacion, Definicion, InfoAdicional, TasasPlan, Vencimientos } from './components';
import { SectionHeading } from '../../components/common';
import { useForm, useFormErrors, useLista, useManagedContext, useNav, useNavigateWithFilters, useUnsavedChangesGuard } from '../../components/hooks';
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';
import { initialFormValues, initialOtherValues, formatDataForPost, TIPOS_VINCULO_CUENTA } from './utils';
import { ServerRequest } from '../../utils/apiweb';
import { CloneObject } from '../../utils/helpers';
import { getDateId } from '../../utils/convert';

function PlanPagoDefinicionView() {
    const params = useParams()
    const location = useLocation()
    const previousFilters = location.state?.filters
    const navigate = useNav()
    const navigateWithFilters = useNavigateWithFilters(() => previousFilters || null)
    const { setIsLoading } = useManagedContext()
    
    const dispatch = useDispatch();
    const dataTagger = useSelector( (state) => state.dataTagger.data );

    const [formValues, handleInput, , setFormValues] = useForm(initialFormValues)
    const [otherValues, setOtherValues] = useState(initialOtherValues)

    const [pendingChange, setPendingChange] = useState(false);
    const [idUsuarioCreacion, setIdUsuarioCreacion] = useState(null)

    const [state, setState] = useState({
        processKey: `PlanPagoDefinicion_${params.id??0}_${getDateId()}`,
        id: params.id ? parseInt(params.id) : 0,
        mode: params.mode,
        utilizada: false,
        fechaPrimeraUtilizacion: null,
        accordions: {
            definicion: true,
            tasasPlan: false,
            vencimientos: false,
            condAplicacion: false,
            criteriosFinanciacion: false,
            criteriosCaducidad: false,
            info: false
        },
        tabActive: "definicion"
    });

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange);

    useEffect(() => {
        if (params.id) {
            setFormValues({...formValues, id: params.id})
            setIsLoading(true)

            const onSuccess = (response) => {
                response.json()
                .then(data => {
                    if (data.planPagoDefinicion.fechaDesde) data.planPagoDefinicion.fechaDesde = new Date(data.planPagoDefinicion.fechaDesde);
                    if (data.planPagoDefinicion.fechaHasta) data.planPagoDefinicion.fechaHasta = new Date(data.planPagoDefinicion.fechaHasta);
                    if (data.planPagoDefinicion.fechaDesdeAlcanceTemporal) data.planPagoDefinicion.fechaDesdeAlcanceTemporal = new Date(data.planPagoDefinicion.fechaDesdeAlcanceTemporal);
                    if (data.planPagoDefinicion.fechaHastaAlcanceTemporal) data.planPagoDefinicion.fechaHastaAlcanceTemporal = new Date(data.planPagoDefinicion.fechaHastaAlcanceTemporal);
                    if (data.planPagoDefinicion.fechaCreacion) data.planPagoDefinicion.fechaCreacion = new Date(data.planPagoDefinicion.fechaCreacion);
                    data.archivos.forEach(x => {
                        if (x.fecha) x.fecha = new Date(x.fecha);
                    });
                    data.observaciones.forEach(x => {
                        if (x.fecha) x.fecha = new Date(x.fecha);
                    });

                    const { utilizada, fechaPrimeraUtilizacion, ...dataDefinicion } = data

                    setFormValues(data.planPagoDefinicion)
                    setIdUsuarioCreacion(data.planPagoDefinicion.idUsuarioCreacion);
                    setState(prev => ({
                        ...prev,
                        utilizada: utilizada === true,
                        fechaPrimeraUtilizacion: fechaPrimeraUtilizacion ? new Date(fechaPrimeraUtilizacion) : null
                    }))

                    setOtherValues({...dataDefinicion, planPagoDefinicion: undefined})
                    dispatch(dataTaggerActionSet(state.processKey, {
                        Archivo: data.archivos,
                        Observacion: data.observaciones,
                        Etiqueta: data.etiquetas
                    }))
                    setIsLoading(false)
                })
            }

            ServerRequest(
                REQUEST_METHOD.GET,
                null,
                true,
                APIS.URLS.PLAN_PAGO_DEFINICION,
                `/${params.id}`,
                null,
                onSuccess,
                onNoSuccess,
                onError,
            );
        }
        else {
            dispatch(dataTaggerActionSet(state.processKey, {
                Archivo: [],
                Observacion: [],
                Etiqueta: []
            }))
        }
    }, [params.id, params.mode])

    const onNoSuccess = (response) => {
        response.json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setIsLoading(false)
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                setIsLoading(false)
            })
    }

    const onError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message);
        setIsLoading(false)
    }

    const getErrorsAlta = () => {
        const errors = {}

        if (formValues.codigo.length < 1) errors.codigo = VALIDATION_MESSAGE.REQUIRED
        if (formValues.descripcion.length < 1) errors.descripcion = VALIDATION_MESSAGE.REQUIRED
        if (formValues.idTipoPlanPago === 0) errors.idTipoPlanPago = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoTributo === 0) errors.idTipoTributo = VALIDATION_MESSAGE.REQUIRED_OPTION

        return errors
    }

    const getErrors = () => {
        const errors = getErrorsAlta()

        if (formValues.idEstadoPlanPagoDefinicion <= 0) errors.idEstadoPlanPagoDefinicion = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.fechaDesde === null) errors.fechaDesde = VALIDATION_MESSAGE.REQUIRED_DATE
        if (formValues.idTasaPlanPago <= 0) errors.idTasaPlanPago = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idSubTasaPlanPago <= 0) errors.idSubTasaPlanPago = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoVencimientoAnticipo <= 0) errors.idTipoVencimientoAnticipo = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoVencimientoCuota1 <= 0) errors.idTipoVencimientoCuota1 = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoVencimientoCuotas <= 0) errors.idTipoVencimientoCuotas = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoAlcanceTemporal <= 0) errors.idTipoAlcanceTemporal = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.periodicidad <= 0) errors.periodicidad = 'Ingresá un número mayor a cero'
        if (formValues.idTipoCalculoInteres <= 0) errors.idTipoCalculoInteres = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoCobroAccesorios <= 0) errors.idTipoCobroAccesorios = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idViaConsolidacion <= 0) errors.idViaConsolidacion = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoTratamientoPagos <= 0) errors.idTipoTratamientoPagos = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idTipoTratamientoDebitoAutomatico <= 0) errors.idTipoTratamientoDebitoAutomatico = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.cuotaDesde <= 0) errors.cuotaDesde = 'Ingresá un número mayor a cero'
        if (formValues.cuotaHasta <= 0) errors.cuotaHasta = 'Ingresá un número mayor a cero'

        return errors
    }

    const { errorOf: errorOfAlta, validate: validateAlta } = useFormErrors(getErrorsAlta)
    const { errorOf: errorOfModificacion, validate: validateModificacion } = useFormErrors(getErrors)

    const errorOf = (name) => errorOfModificacion(name) ?? errorOfAlta(name)

    const openAccordion = (field) => setState(prev => ({...prev, accordions: {...prev.accordions, [field]: true}}))

    const abrirAccordionDelError = (errors) => {
        if (errors.codigo || errors.descripcion || errors.idTipoPlanPago || errors.idTipoTributo ||
            errors.idEstadoPlanPagoDefinicion || errors.fechaDesde || errors.idViaConsolidacion ||
            errors.idTipoTratamientoDebitoAutomatico) openAccordion('definicion')
        if (errors.idTasaPlanPago || errors.idSubTasaPlanPago) openAccordion('tasasPlan')
        if (errors.idTipoVencimientoAnticipo || errors.idTipoVencimientoCuota1 || errors.idTipoVencimientoCuotas) openAccordion('vencimientos')
        if (errors.idTipoAlcanceTemporal) openAccordion('condAplicacion')
        if (errors.periodicidad || errors.idTipoCalculoInteres || errors.idTipoCobroAccesorios || errors.cuotaDesde || errors.cuotaHasta) openAccordion('criteriosFinanciacion')
        if (errors.idTipoTratamientoPagos) openAccordion('criteriosCaducidad')
    }

    const [getListLista, , listaReady] = useLista({
        listas: Object.values(TIPOS_VINCULO_CUENTA),
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: {
            key: 'TipoVinculos',
            timeout: 0
        },
    })

    const tiposVinculoCuenta = useMemo(
        () => getListLista(TIPOS_VINCULO_CUENTA[formValues.idTipoTributo]),
        [formValues.idTipoTributo, listaReady]
    )

    const tieneRelacionesPermitidas = () => otherValues.planPagosDefinicionTipoVinculoCuenta
        .some(item => item.state !== 'r' && (!listaReady || tiposVinculoCuenta.some(tipo => tipo.id === item.idTipoVinculoCuenta)))

    const handleInputProxy = (event) => {
        handleInput(event);
        setPendingChange(true);
    }   

    const addPlan = () => {
        if (!validateAlta()) {
            openAccordion('definicion')
            return
        }

        setIsLoading(true)
        
        const onSuccess = (response) => {
            response.json()
                .then((data) => {
                    setPendingChange(false);
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Definición de plan de pago ingresado correctamente", () => {
                        dispatch(dataTaggerActionClear(state.processKey))
                        setIsLoading(false)
                        allowNavigation()
                        navigate({
                            to: `/plan-pago-definicion/edit/${data.planPagoDefinicion.id}`,
                            refresh: true,
                            replace: true,
                        })
                    });
                })
                .catch((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error)
                    setIsLoading(false)
                });
        }

        const planPagoDefinicion = {...formValues, noAplicaDeudoresDDJJ: [TRIBUTO_TYPE.COMERCIOS, TRIBUTO_TYPE.FONDEADEROS].includes(formValues.idTipoTributo) && formValues.noAplicaDeudoresDDJJ === true};

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.PLAN_PAGO_DEFINICION,
            null,
            // formatDataForPost(formValues, otherValues),
            { planPagoDefinicion: planPagoDefinicion, ...otherValues },
            onSuccess,
            onNoSuccess,
            onError,
        );
    }

    const modifyPlan = () => {
        abrirAccordionDelError(getErrors())

        if (!validateModificacion())
            return

        if (!tieneRelacionesPermitidas()) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Falta definir las relaciones permitidas')
            openAccordion('definicion')
            return
        }

        setIsLoading(true)
        
        const onSuccess = (response) => {
            response.json()
                .then((data) => {
                    setPendingChange(false);
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Definición de plan de pago actualizado correctamente", () => {
                        if (data.planPagoDefinicion.fechaDesde) data.planPagoDefinicion.fechaDesde = new Date(data.planPagoDefinicion.fechaDesde);
                        if (data.planPagoDefinicion.fechaHasta) data.planPagoDefinicion.fechaHasta = new Date(data.planPagoDefinicion.fechaHasta);
                        if (data.planPagoDefinicion.fechaDesdeAlcanceTemporal) data.planPagoDefinicion.fechaDesdeAlcanceTemporal = new Date(data.planPagoDefinicion.fechaDesdeAlcanceTemporal);
                        if (data.planPagoDefinicion.fechaHastaAlcanceTemporal) data.planPagoDefinicion.fechaHastaAlcanceTemporal = new Date(data.planPagoDefinicion.fechaHastaAlcanceTemporal);
                        if (data.planPagoDefinicion.fechaCreacion) data.planPagoDefinicion.fechaCreacion = new Date(data.planPagoDefinicion.fechaCreacion);
                        data.archivos.forEach(x => {
                            if (x.fecha) x.fecha = new Date(x.fecha);
                        });
                        data.observaciones.forEach(x => {
                            if (x.fecha) x.fecha = new Date(x.fecha);
                        });

                        const { utilizada, fechaPrimeraUtilizacion, ...dataDefinicion } = data

                        setFormValues(data.planPagoDefinicion)
                        setOtherValues({...dataDefinicion, planPagoDefinicion: undefined})
                        dispatch(dataTaggerActionSet(state.processKey, {
                            Archivo: data.archivos,
                            Observacion: data.observaciones,
                            Etiqueta: data.etiquetas
                        }))
                        setIsLoading(false)
                    })
                })
                .catch((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error)
                    setIsLoading(false)
                });
        }

        const partialOtherValues = CloneObject(otherValues);
        const allOtherValues = {...partialOtherValues,
            archivos: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Archivo : [],
            observaciones: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Observacion : [],
            etiquetas: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Etiqueta : []
        }

        const planPagoDefinicion = {...formValues, noAplicaDeudoresDDJJ: [TRIBUTO_TYPE.COMERCIOS, TRIBUTO_TYPE.FONDEADEROS].includes(formValues.idTipoTributo) && formValues.noAplicaDeudoresDDJJ === true};

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.PLAN_PAGO_DEFINICION,
            `/${params.id}`,
            formatDataForPost(planPagoDefinicion, allOtherValues),
            onSuccess,
            onNoSuccess,
            onError,
        );
    }

    const paramsSecciones = state.utilizada ? {...params, mode: OPERATION_MODE.VIEW} : params

    const buildSectionProps = field => ({
        toggle: () => setState(prev => ({...prev, accordions: {...prev.accordions, [field]: !prev.accordions[field]}})),
        open: state.accordions[field],
        formValues,
        setFormValues,
        handleInputProxy,
        errorOf,
        params: paramsSecciones,
        tiposVinculoCuenta,
    })

    const returnPlan = () => {
        if (!pendingChange) dispatch(dataTaggerActionClear(state.processKey));
        navigateWithFilters(`/planes-pago-definicion`);
    }

    return <>

        <SectionHeading
            titles={[
                { title: 'Financiación' },
                { title: 'Definición de planes de pago', url: '/planes-pago-definicion', },
                { title: params.mode === OPERATION_MODE.NEW ? 'Nueva' : `Código: ${formValues.codigo}` }
            ]}
        />

        <div className="m-top-20">
            <section className='section-accordion'>
                <div className="m-top-20 m-bottom-20">  
                    {state.utilizada && (
                        <div className='row'>
                            <div className="col-12 m-bottom-20">
                                <span className="busqueda-mensaje m-left-20">
                                    <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                                    La definición ya fue utilizada por un plan de pago; solo se puede modificar la vigencia, la descripción y el estado
                                </span>
                            </div>
                        </div>
                    )}
                    <Definicion {...buildSectionProps('definicion')} {...{params, otherValues, setOtherValues, setPendingChange, idUsuarioCreacion}} utilizada={state.utilizada} fechaPrimeraUtilizacion={state.fechaPrimeraUtilizacion} />
                    {(params.mode !== OPERATION_MODE.NEW) && <>
                        <TasasPlan {...buildSectionProps('tasasPlan')} />
                        <Vencimientos {...buildSectionProps('vencimientos')} {...{setPendingChange}} />
                        <CondicionesAplicacion {...buildSectionProps('condAplicacion')} {...{otherValues, setOtherValues, setPendingChange}} />
                        <CriteriosFinanciacion {...buildSectionProps('criteriosFinanciacion')} {...{otherValues, setOtherValues, setPendingChange}} />
                        <CriteriosCaducidad {...buildSectionProps('criteriosCaducidad')} {...{otherValues, setOtherValues, setPendingChange}} />
                        <InfoAdicional {...buildSectionProps('infoAdicional')}
                            title="Información adicional de definición de planes de pago"
                            entity="PlanPagoDefinicion"
                            idEntity={state.id}
                            mode={state.mode}
                            processKey={state.processKey}
                            setPendingChange={setPendingChange}
                        />
                    </>}
                </div>
            </section>
        </div>

        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                <button className="btn back-button float-start" onClick={ (event) => returnPlan() }>Volver</button>
                {params.mode !== OPERATION_MODE.VIEW && (
                    <button
                        className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                        onClick={params.mode === OPERATION_MODE.NEW ? addPlan : modifyPlan}
                        disabled={!pendingChange}
                    >
                        {params.mode === OPERATION_MODE.NEW ? 'Siguiente' : 'Guardar'}
                    </button>
                )}
            </div>
        </footer>

    </>
}

export default PlanPagoDefinicionView;
